import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Plus, Trash2, Shield, Eye, Clipboard,
  UserCheck, UserX, Search, Leaf, Flame, Crosshair
} from 'lucide-react'
import {
  getUsers, createUser, updateUserRole, deactivateUser, getRoles, getOwnerSegments, linkOwnerSegmentUser,
} from '../../services/api'
import PageHeader from '../../components/PageHeader'
import { useAuth } from '../../contexts/AuthContext'
import { ROLE_LABELS } from '../../lib/permissions'

export default function UserManagement({ ranchId }) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const queryClient = useQueryClient()
  const { userId } = useAuth()

  // Fetch users
  const { data: users, isLoading } = useQuery({
    queryKey: ['users', ranchId, roleFilter],
    queryFn: () => getUsers(ranchId, { role: roleFilter || undefined }),
  })

  // Fetch roles
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  })

  // Owner segments with their account link (Phase 4a). The link is what scopes an
  // owner's dashboard card and 'my cattle' to THEIR animals, so it is set here, by
  // the manager, never guessed from a name.
  const { data: segments } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }),
    enabled: !!ranchId,
  })
  const linkMutation = useMutation({
    mutationFn: ({ segmentId, targetUserId, replace }) => linkOwnerSegmentUser(segmentId, targetUserId, { replace }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ownerSegments'] })
      queryClient.invalidateQueries({ queryKey: ['smartkraalRegMine'] })
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail
      alert(typeof detail === 'string' ? detail : 'Could not change the segment link.')
    },
  })
  const linkSegment = (segment, user) => {
    let replace = false
    if (segment.user_id && segment.user_id !== user.id) {
      if (!confirm(`"${segment.name}" is linked to ${segment.user_name || 'another account'}. Move it to ${user.full_name}?`)) return
      replace = true
    }
    linkMutation.mutate({ segmentId: segment.id, targetUserId: user.id, replace })
  }
  const unlinkSegment = (segment) => {
    if (!confirm(`Unlink "${segment.name}"? Its owner will no longer see these animals as theirs.`)) return
    linkMutation.mutate({ segmentId: segment.id, targetUserId: null, replace: false })
  }

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (data) => createUser(data, userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['users'])
      setShowAddModal(false)
    },
  })

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, newRole }) => updateUserRole(userId, ranchId, newRole, userId),
    onSuccess: () => queryClient.invalidateQueries(['users']),
  })

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (userId) => deactivateUser(userId, ranchId, userId),
    onSuccess: () => queryClient.invalidateQueries(['users']),
  })

  const filteredUsers = users?.filter(user => 
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'manager':
      case 'owner':
        return 'bg-purple-100 text-purple-800'
      case 'viewer':
        return 'bg-blue-100 text-blue-800'
      case 'herdsman':
        return 'bg-green-100 text-green-800'
      case 'bush_clearer':
        return 'bg-red-100 text-red-800'
      case 'biochar_operator':
        return 'bg-orange-100 text-orange-800'
      case 'land_monitor':
        return 'bg-lime-100 text-lime-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'manager':
      case 'owner':
        return <Shield className="h-4 w-4" />
      case 'viewer':
        return <Eye className="h-4 w-4" />
      case 'herdsman':
        return <Clipboard className="h-4 w-4" />
      case 'bush_clearer':
        return <Leaf className="h-4 w-4" />
      case 'biochar_operator':
        return <Flame className="h-4 w-4" />
      case 'land_monitor':
        return <Crosshair className="h-4 w-4" />
      default:
        return <Users className="h-4 w-4" />
    }
  }

  return (
    <div className="p-6">
      <PageHeader
        title="User Management"
        subtitle="Manage team members and their permissions"
        icon={Users}
        actions={
          <button
            onClick={() => setShowAddModal(true)}
            className="btn bg-green-600 text-white hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        }
      />

      {/* Filters */}
      <div className="section-card p-4 mb-6">
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="manager">Manager</option>
            <option value="herdsman">Herdsman</option>
            <option value="viewer">Viewer</option>
            <option value="bush_clearer">Bush Clearer</option>
            <option value="biochar_operator">Biochar Operator</option>
            <option value="land_monitor">Land Monitor</option>
          </select>
        </div>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-earth-50 rounded-xl border border-earth-200">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('owner')}`}>Owner</span>
          <span className="text-sm text-gray-600">Approves finance</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('manager')}`}>Manager</span>
          <span className="text-sm text-gray-600">Approves herdsman inputs, runs ops</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('herdsman')}`}>Herdsman</span>
          <span className="text-sm text-gray-600">Enters cattle + grazing data</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('viewer')}`}>Viewer</span>
          <span className="text-sm text-gray-600">Read-only</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('bush_clearer')}`}>Bush Clearer</span>
          <span className="text-sm text-gray-600">Assigned lantana clearances</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('biochar_operator')}`}>Biochar Operator</span>
          <span className="text-sm text-gray-600">Assigned biochar batches</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('land_monitor')}`}>Land Monitor</span>
          <span className="text-sm text-gray-600">Owns land monitoring tab</span>
        </div>
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="text-center py-12">Loading users...</div>
      ) : (
        <div className="section-card">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-earth-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase tracking-wider">
                  Owns segment(s)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-earth-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers?.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-green-600 font-medium">
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.full_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email || '-'}</div>
                    <div className="text-sm text-gray-500">{user.phone_number || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role_at_ranch)}`}>
                      {getRoleIcon(user.role_at_ranch)}
                      {ROLE_LABELS[user.role_at_ranch] || user.role_at_ranch}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <OwnerSegmentsCell user={user} segments={segments} onLink={linkSegment}
                                       onUnlink={unlinkSegment} busy={linkMutation.isPending} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.permissions?.map((perm) => (
                        <span key={perm} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {perm}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        <UserCheck className="h-3 w-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        <UserX className="h-3 w-3" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <select
                        value={user.role_at_ranch}
                        onChange={(e) => updateRoleMutation.mutate({
                          userId: user.id,
                          newRole: e.target.value
                        })}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                        disabled={user.id === userId}
                      >
                        <option value="owner">Owner</option>
                        <option value="manager">Manager</option>
                        <option value="herdsman">Herdsman</option>
                        <option value="viewer">Viewer</option>
                        <option value="bush_clearer">Bush Clearer</option>
                        <option value="biochar_operator">Biochar Operator</option>
                        <option value="land_monitor">Land Monitor</option>
                      </select>
                      {user.id !== userId && (
                        <button
                          onClick={() => {
                            if (confirm('Deactivate this user?')) {
                              deactivateMutation.mutate(user.id)
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          ranchId={ranchId}
          onClose={() => setShowAddModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}
    </div>
  )
}

// The owner-segment account link on an owner's row (Phase 4a): the segments this
// account holds as chips (x unlinks) and a select to add one. A segment another
// account holds is offered with that name; choosing it asks before moving it.
function OwnerSegmentsCell({ user, segments, onLink, onUnlink, busy }) {
  const isOwner = user.role_at_ranch === 'owner'
  const mine = (segments || []).filter((s) => s.user_id === user.id)
  const others = isOwner ? (segments || []).filter((s) => s.user_id !== user.id) : []
  // a link made while the person was an owner stays on the row after a re-role so the
  // manager can see and undo it; the backend ignores it for scoping until they are an owner again
  if (!isOwner && mine.length === 0) return <span className="text-xs text-earth-400">—</span>
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`owner-segments-${user.id}`}>
      {mine.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs border border-primary-100">
          {s.name}
          <button type="button" onClick={() => onUnlink(s)} title={`Unlink ${s.name}`} disabled={busy}
            className="hover:text-red-700 font-bold leading-none">×</button>
        </span>
      ))}
      {others.length > 0 && (
        <select value="" disabled={busy} aria-label={`Link an owner segment to ${user.full_name}`}
          onChange={(e) => { const s = others.find((x) => x.id === e.target.value); if (s) onLink(s, user) }}
          className="text-xs border border-gray-300 rounded px-1.5 py-0.5 text-earth-600">
          <option value="">{mine.length ? 'Add…' : 'Owns segment…'}</option>
          {others.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}{s.user_id ? ` (linked to ${s.user_name || 'another account'})` : ''}
            </option>
          ))}
        </select>
      )}
      {!isOwner && mine.length > 0 && (
        <span className="text-xs text-amber-700" title="The link only scopes an owner's view; unlink it or make them an owner again">not an owner</span>
      )}
      {isOwner && mine.length === 0 && others.length === 0 && <span className="text-xs text-earth-400">No segments yet</span>}
    </div>
  )
}

function AddUserModal({ ranchId, onClose, onSubmit, isLoading }) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    role: 'herdsman',
    role_at_ranch: 'herdsman',
    language_pref: 'en',
    ranch_id: ranchId,
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add New User</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              required
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="+256..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              required
              value={formData.role_at_ranch}
              onChange={(e) => setFormData({
                ...formData,
                role: e.target.value,
                role_at_ranch: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="herdsman">Herdsman — Enters cattle + grazing data</option>
              <option value="manager">Manager — Approves herdsman inputs, runs ops</option>
              <option value="owner">Owner — Approves finance</option>
              <option value="viewer">Viewer — Read-only</option>
              <option value="bush_clearer">Bush Clearer — Assigned lantana clearances</option>
              <option value="biochar_operator">Biochar Operator — Assigned biochar batches</option>
              <option value="land_monitor">Land Monitor — Owns land monitoring tab</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
